import React, { useMemo, useState, useCallback, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Copy,
  Download,
  Check,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  X,
  Eye,
} from 'lucide-react';
import { useAppStore } from '../store';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'estado-resultados';
const FIRST_MONTH_YEAR = 2026;
const FIRST_MONTH = 1; // Febrero
const INGRESOS_KEYS = [
  { key: 'production', label: 'Producciones Pro' },
  { key: 'express', label: 'Producciones Express' },
  { key: 'sample', label: 'Muestras Pro' },
  { key: 'express_sample', label: 'Muestras Express' },
  { key: 'notmerch', label: 'Producciones Notmerch' },
  { key: 'other', label: 'Otros ingresos' },
] as const;

const COSTOS_KEYS = [
  { key: 'cmv_production', label: 'CMV Producciones Pro', manual: false },
  { key: 'cmv_express', label: 'CMV Producciones Express', manual: true },
  { key: 'cmv_sample', label: 'CMV Muestras Pro', manual: false },
  { key: 'cmv_express_sample', label: 'CMV Muestras Express', manual: true },
  { key: 'cmv_notmerch', label: 'CMV Notmerch', manual: true },
  { key: 'otros_costos', label: 'Otros costos', manual: true },
] as const;

const getInvoiceNet = (inv: any) => {
  const base =
    inv.tax != null && inv.tax > 0
      ? inv.total - inv.tax
      : (inv.subtotal ?? inv.total) ?? 0;
  const rate =
    inv.currency === 'USD' && inv.exchange_rate != null ? inv.exchange_rate : 1;
  return base * rate;
};

const EstadoResultados: React.FC = () => {
  const { userRole } = useAuth();
  const {
    invoices,
    productions,
    samples,
    express,
    expressSamples,
    productionTasks,
    sampleTasks,
    expenses,
    expenseCategories,
    suppliers,
    clients,
  } = useAppStore();

  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const isPrevBeforeFirst =
    prevMonth.getFullYear() < FIRST_MONTH_YEAR ||
    (prevMonth.getFullYear() === FIRST_MONTH_YEAR &&
      prevMonth.getMonth() < FIRST_MONTH);
  const initialDate = isPrevBeforeFirst
    ? new Date(FIRST_MONTH_YEAR, FIRST_MONTH, 1)
    : prevMonth;
  const [year, setYear] = useState(initialDate.getFullYear());
  const [month, setMonth] = useState(initialDate.getMonth());

  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

  const [manualValues, setManualValues] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved).manualValues ?? {} : {};
    } catch {
      return {};
    }
  });

  const [checks, setChecks] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved).checks ?? {} : {};
    } catch {
      return {};
    }
  });

  const saveToStorage = useCallback(
    (updates: { manualValues?: Record<string, number>; checks?: Record<string, boolean> }) => {
      const prev = (() => {
        try {
          const s = localStorage.getItem(STORAGE_KEY);
          return s ? JSON.parse(s) : {};
        } catch {
          return {};
        }
      })();
      const next = {
        ...prev,
        manualValues: { ...prev.manualValues, ...updates.manualValues },
        checks: { ...prev.checks, ...updates.checks },
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    },
    []
  );

  const setManualValue = useCallback(
    (key: string, value: number) => {
      const next = { ...manualValues, [`${monthKey}:${key}`]: value };
      setManualValues(next);
      saveToStorage({ manualValues: next });
    },
    [monthKey, manualValues, saveToStorage]
  );

  const toggleCheck = useCallback(
    (key: string) => {
      const fullKey = `${monthKey}:${key}`;
      const next = { ...checks, [fullKey]: !checks[fullKey] };
      setChecks(next);
      saveToStorage({ checks: next });
    },
    [monthKey, checks, saveToStorage]
  );

  const startDate = useMemo(
    () => new Date(year, month, 1).toISOString().split('T')[0],
    [year, month]
  );
  const endDate = useMemo(() => {
    const d = new Date(year, month + 1, 0);
    return d.toISOString().split('T')[0];
  }, [year, month]);

  const eerr = useMemo(() => {
    const clientInvoices = invoices.filter(
      (i) =>
        !i.voided &&
        i.entity_type === 'client' &&
        i.date >= startDate &&
        i.date <= endDate
    );

    const ingresos: Record<string, number> = {};
    INGRESOS_KEYS.forEach(({ key }) => {
      ingresos[key] = clientInvoices
        .filter((i) => (i.sale_type ?? 'production') === key)
        .reduce((s, i) => s + getInvoiceNet(i), 0);
    });

    const prodInPeriod = productions.filter((p) => {
      const d = (p as { start_date?: string }).start_date ?? p.created_at?.slice(0, 10) ?? '';
      return d >= startDate && d <= endDate && !['cancelled'].includes(p.status ?? '');
    });
    const sampInPeriod = samples.filter((s) => {
      const d = (s as { start_date?: string }).start_date ?? s.created_at?.slice(0, 10) ?? '';
      return d >= startDate && d <= endDate && !['cancelled'].includes(s.status ?? '');
    });
    const exprInPeriod = express.filter((e) => {
      const d = (e as { start_date?: string }).start_date ?? e.created_at ?? (e as { date?: string }).date ?? '';
      const dStr = typeof d === 'string' ? d.slice(0, 10) : '';
      return dStr >= startDate && dStr <= endDate;
    });
    const exprSampInPeriod = expressSamples.filter((es) => {
      const d = (es as { start_date?: string }).start_date ?? es.created_at ?? (es as { date?: string }).date ?? '';
      const dStr = typeof d === 'string' ? d.slice(0, 10) : '';
      return dStr >= startDate && dStr <= endDate;
    });

    const cmvProduction = productionTasks
      .filter((t) => prodInPeriod.some((p) => p.id === t.production_id))
      .reduce((s, t) => s + (t.cost ?? 0), 0);

    const cmvSample = sampleTasks
      .filter((t) => sampInPeriod.some((s) => s.id === t.sample_id))
      .reduce((s, t) => s + (t.cost ?? 0), 0);

    const periodExpenses = expenses.filter(
      (e) => e.date >= startDate && e.date <= endDate
    );
    const gastosByCategory: Record<string, number> = {};
    periodExpenses.forEach((exp) => {
      const name =
        expenseCategories.find((c) => c.id === exp.category_id)?.name ??
        'Sin categoría';
      gastosByCategory[name] = (gastosByCategory[name] ?? 0) + (exp.amount ?? exp.total ?? 0);
    });

    const costos: Record<string, number> = {
      cmv_production: cmvProduction,
      cmv_express: manualValues[`${monthKey}:cmv_express`] ?? 0,
      cmv_sample: cmvSample,
      cmv_express_sample: manualValues[`${monthKey}:cmv_express_sample`] ?? 0,
      cmv_notmerch: manualValues[`${monthKey}:cmv_notmerch`] ?? 0,
      otros_costos: manualValues[`${monthKey}:otros_costos`] ?? 0,
    };

    return {
      ingresos,
      costos,
      gastosByCategory,
      prodInPeriod,
      sampInPeriod,
      exprInPeriod,
      exprSampInPeriod,
    };
  }, [
    invoices,
    productions,
    samples,
    express,
    expressSamples,
    productionTasks,
    sampleTasks,
    expenses,
    expenseCategories,
    startDate,
    endDate,
    monthKey,
    manualValues,
  ]);

  const totalIngresos = Object.values(eerr.ingresos).reduce((a, b) => a + b, 0);
  const totalCostos = Object.values(eerr.costos).reduce((a, b) => a + b, 0);
  const totalGastos = Object.values(eerr.gastosByCategory).reduce(
    (a, b) => a + b,
    0
  );
  const utilidadBruta = totalIngresos - totalCostos;
  const utilidadNeta = utilidadBruta - totalGastos;

  const allClosed = useMemo(() => {
    const prodClosed =
      eerr.prodInPeriod.length === 0 ||
      eerr.prodInPeriod.every((p) => (p.status ?? '') === 'shipped');
    const sampClosed =
      eerr.sampInPeriod.length === 0 ||
      eerr.sampInPeriod.every((s) =>
        ['shipped/despatched'].includes(s.status ?? '')
      );
    const exprClosed =
      eerr.exprInPeriod.length === 0 ||
      eerr.exprInPeriod.every((e) => (e.status ?? '') === 'shipped');
    const exprSampClosed =
      eerr.exprSampInPeriod.length === 0 ||
      eerr.exprSampInPeriod.every((es) => (es.status ?? '') === 'shipped');
    return prodClosed && sampClosed && exprClosed && exprSampClosed;
  }, [
    eerr.prodInPeriod,
    eerr.sampInPeriod,
    eerr.exprInPeriod,
    eerr.exprSampInPeriod,
  ]);

  const checkKey = (k: string) => checks[`${monthKey}:${k}`] ?? false;

  const copyTable = useCallback(() => {
    const rows: string[][] = [
      ['Estado de Resultados', monthKey],
      [],
      ['INGRESOS', 'Monto', '%'],
      ...INGRESOS_KEYS.map(({ key, label }) => [
        label,
        String(eerr.ingresos[key] ?? 0),
        totalIngresos > 0
          ? String(
              Math.round(
                ((eerr.ingresos[key] ?? 0) / totalIngresos) * 10000
              ) / 100
            ) + '%'
          : '0%',
      ]),
      ['Total Ingresos', String(totalIngresos), '100%'],
      [],
      ['COSTOS', 'Monto', '%'],
      ...COSTOS_KEYS.map(({ key, label }) => [
        label,
        String(eerr.costos[key] ?? 0),
        totalIngresos > 0
          ? String(
              Math.round(((eerr.costos[key] ?? 0) / totalIngresos) * 10000) /
                100
            ) + '%'
          : '0%',
      ]),
      ['Total Costos', String(totalCostos), ''],
      ['Utilidad Bruta', String(utilidadBruta), ''],
      [],
      ['GASTOS', 'Monto', '%'],
      ...Object.entries(eerr.gastosByCategory).map(([cat, amt]) => [
        cat,
        String(amt),
        totalIngresos > 0
          ? String(Math.round((amt / totalIngresos) * 10000) / 100) + '%'
          : '0%',
      ]),
      ['Total Gastos', String(totalGastos), ''],
      ['Utilidad Neta', String(utilidadNeta), ''],
    ];
    const tsv = rows.map((r) => r.join('\t')).join('\n');
    navigator.clipboard.writeText(tsv);
  }, [monthKey, eerr, totalIngresos, totalCostos, totalGastos, utilidadBruta, utilidadNeta]);

  const exportCSV = useCallback(() => {
    const rows: string[][] = [
      ['Estado de Resultados', monthKey],
      [],
      ['Sección', 'Concepto', 'Monto', 'Porcentaje'],
      ...INGRESOS_KEYS.map(({ key, label }) => [
        'INGRESOS',
        label,
        String((eerr.ingresos[key] ?? 0).toFixed(2)),
        totalIngresos > 0
          ? (
              ((eerr.ingresos[key] ?? 0) / totalIngresos) *
              100
            ).toFixed(2) + '%'
          : '0%',
      ]),
      ['INGRESOS', 'Total Ingresos', String(totalIngresos.toFixed(2)), '100%'],
      [],
      ...COSTOS_KEYS.map(({ key, label }) => [
        'COSTOS',
        label,
        String((eerr.costos[key] ?? 0).toFixed(2)),
        totalIngresos > 0
          ? (
              ((eerr.costos[key] ?? 0) / totalIngresos) *
              100
            ).toFixed(2) + '%'
          : '0%',
      ]),
      ['COSTOS', 'Total Costos', String(totalCostos.toFixed(2)), ''],
      ['COSTOS', 'Utilidad Bruta', String(utilidadBruta.toFixed(2)), ''],
      [],
      ...Object.entries(eerr.gastosByCategory).map(([cat, amt]) => [
        'GASTOS',
        cat,
        String(amt.toFixed(2)),
        totalIngresos > 0
          ? ((amt / totalIngresos) * 100).toFixed(2) + '%'
          : '0%',
      ]),
      ['GASTOS', 'Total Gastos', String(totalGastos.toFixed(2)), ''],
      ['RESULTADO', 'Utilidad Neta', String(utilidadNeta.toFixed(2)), ''],
    ];
    const csv =
      '\uFEFF' +
      rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `estado-resultados-${monthKey}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [monthKey, eerr, totalIngresos, totalCostos, totalGastos, utilidadBruta, utilidadNeta]);

  const [copied, setCopied] = useState(false);
  const [detailModal, setDetailModal] = useState<{
    type: 'ingreso' | 'costo' | 'gasto';
    key: string;
    label: string;
  } | null>(null);

  const getDetailItems = useCallback(
    (type: 'ingreso' | 'costo' | 'gasto', key: string) => {
      const clientInvoices = invoices.filter(
        (i) =>
          !i.voided &&
          i.entity_type === 'client' &&
          i.date >= startDate &&
          i.date <= endDate
      );
      if (type === 'ingreso') {
        const list = clientInvoices.filter(
          (i) => (i.sale_type ?? 'production') === key
        );
        return list.map((inv) => {
          const client =
            inv.entity_type === 'client' && inv.entity_id
              ? clients.find((c) => c.id === inv.entity_id)
              : null;
          return {
            id: inv.id,
            desc: `Factura ${inv.number}`,
            sub: inv.client_name ?? client?.name ?? inv.entity_id ?? '',
            date: inv.date,
            amount: getInvoiceNet(inv),
          };
        });
      }
      if (type === 'costo') {
        if (
          [
            'cmv_express',
            'cmv_express_sample',
            'cmv_notmerch',
            'otros_costos',
          ].includes(key)
        ) {
          return null; // manual
        }
        if (key === 'cmv_production') {
          const prodInPeriod = productions.filter((p) => {
            const d = (p as { start_date?: string }).start_date ?? p.created_at?.slice(0, 10) ?? '';
            return d >= startDate && d <= endDate && !['cancelled'].includes(p.status ?? '');
          });
          return productionTasks
            .filter((t) => prodInPeriod.some((p) => p.id === t.production_id))
            .map((t) => {
              const prod = productions.find((p) => p.id === t.production_id);
              const sup = suppliers.find((s) => s.id === t.supplier_id);
              const prodD = (prod as { start_date?: string })?.start_date ?? prod?.created_at?.slice(0, 10) ?? '';
              return {
                id: t.id,
                desc: prod?.name ?? 'Producción',
                sub: sup?.name ?? 'Proveedor',
                date: prodD,
                amount: t.cost ?? 0,
              };
            });
        }
        if (key === 'cmv_sample') {
          const sampInPeriod = samples.filter((s) => {
            const d = (s as { start_date?: string }).start_date ?? s.created_at?.slice(0, 10) ?? '';
            return d >= startDate && d <= endDate && !['cancelled'].includes(s.status ?? '');
          });
          return sampleTasks
            .filter((t) => sampInPeriod.some((s) => s.id === t.sample_id))
            .map((t) => {
              const samp = samples.find((s) => s.id === t.sample_id);
              const sup = suppliers.find((s) => s.id === t.supplier_id);
              const sampD = (samp as { start_date?: string })?.start_date ?? samp?.created_at?.slice(0, 10) ?? '';
              return {
                id: t.id,
                desc: samp?.name ?? 'Muestra',
                sub: sup?.name ?? 'Proveedor',
                date: sampD,
                amount: t.cost ?? 0,
              };
            });
        }
      }
      if (type === 'gasto') {
        const periodExpenses = expenses.filter(
          (e) => e.date >= startDate && e.date <= endDate
        );
        return periodExpenses
          .filter((exp) => {
            const name =
              expenseCategories.find((c) => c.id === exp.category_id)?.name ??
              'Sin categoría';
            return name === key;
          })
          .map((exp) => ({
            id: exp.id,
            desc: exp.description ?? 'Sin descripción',
            sub: '',
            date: exp.date,
            amount: exp.amount ?? exp.total ?? 0,
          }));
      }
      return [];
    },
    [
      invoices,
      productions,
      samples,
      productionTasks,
      sampleTasks,
      suppliers,
      clients,
      expenses,
      expenseCategories,
      startDate,
      endDate,
    ]
  );

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  if (userRole !== 'Administrador') {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        Solo los Administradores pueden ver el Estado de Resultados.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Estado de Resultados
          </h1>
          <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1">
            <button
              onClick={() => {
                if (
                  year > FIRST_MONTH_YEAR ||
                  (year === FIRST_MONTH_YEAR && month > FIRST_MONTH)
                ) {
                  if (month === 0) {
                    setMonth(11);
                    setYear((y) => y - 1);
                  } else setMonth((m) => m - 1);
                }
              }}
              disabled={
                year < FIRST_MONTH_YEAR ||
                (year === FIRST_MONTH_YEAR && month <= FIRST_MONTH)
              }
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[140px] text-center font-medium">
              {monthNames[month]} {year}
            </span>
            <button
              onClick={() => {
                if (month === 11) {
                  setMonth(0);
                  setYear((y) => y + 1);
                } else setMonth((m) => m + 1);
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          {allClosed && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200">
              <Check className="h-3.5 w-3.5" />
              Mes cerrado
            </span>
          )}
          {!allClosed &&
            (eerr.prodInPeriod.length > 0 ||
              eerr.sampInPeriod.length > 0 ||
              eerr.exprInPeriod.length > 0 ||
              eerr.exprSampInPeriod.length > 0) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
              <AlertCircle className="h-3.5 w-3.5" />
              Pendientes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              copyTable();
              setCopied(true);
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? 'Copiado' : 'Copiar'}
          </button>
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Download className="h-4 w-4" />
            CSV
          </button>
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Ingresos
          </div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">
            {formatCurrency(totalIngresos)}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Utilidad Bruta
          </div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">
            {formatCurrency(utilidadBruta)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Margen{' '}
            {totalIngresos > 0
              ? Math.round((utilidadBruta / totalIngresos) * 100)
              : 0}
            %
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Utilidad Neta
          </div>
          <div
            className={`text-lg font-semibold mt-0.5 ${
              utilidadNeta >= 0
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {formatCurrency(utilidadNeta)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {totalIngresos > 0
              ? Math.round((utilidadNeta / totalIngresos) * 100)
              : 0}
            % sobre ingresos
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Gastos
          </div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">
            {formatCurrency(totalGastos)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {totalIngresos > 0
              ? Math.round((totalGastos / totalIngresos) * 100)
              : 0}
            % de ingresos
          </div>
        </div>
      </div>

      {/* Tabla EERR */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-500 dark:text-gray-400 w-10" />
                <th className="text-left py-3 px-4 font-medium text-gray-500 dark:text-gray-400">
                  Concepto
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400">
                  Monto
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 w-20">
                  %
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100 dark:border-gray-700/50">
                <td colSpan={4} className="py-2 px-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/80">
                  INGRESOS
                </td>
              </tr>
              {INGRESOS_KEYS.map(({ key, label }) => (
                <tr
                  key={key}
                  onClick={() => setDetailModal({ type: 'ingreso', key, label })}
                  className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 cursor-pointer group"
                >
                  <td className="py-2 px-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleCheck(`ingreso:${key}`)}
                      className={`w-5 h-5 rounded border flex items-center justify-center ${
                        checkKey(`ingreso:${key}`)
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {checkKey(`ingreso:${key}`) && <Check className="h-3 w-3" />}
                    </button>
                  </td>
                  <td className="py-2 px-4 flex items-center gap-1">
                    {label}
                    <Eye className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </td>
                  <td className="py-2 px-4 text-right font-mono">
                    {formatCurrency(eerr.ingresos[key] ?? 0)}
                  </td>
                  <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">
                    {totalIngresos > 0
                      ? (
                          ((eerr.ingresos[key] ?? 0) / totalIngresos) *
                          100
                        ).toFixed(1) + '%'
                      : '-'}
                  </td>
                </tr>
              ))}
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/60 font-medium">
                <td className="py-3 px-4" />
                <td className="py-3 px-4">Total Ingresos</td>
                <td className="py-3 px-4 text-right font-mono">
                  {formatCurrency(totalIngresos)}
                </td>
                <td className="py-3 px-4 text-right">100%</td>
              </tr>

              <tr className="border-b border-gray-100 dark:border-gray-700/50">
                <td colSpan={4} className="py-2 px-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/80">
                  COSTOS
                </td>
              </tr>
              {COSTOS_KEYS.map(({ key, label, manual }) => (
                <tr
                  key={key}
                  onClick={() => setDetailModal({ type: 'costo', key, label })}
                  className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 cursor-pointer group"
                >
                  <td className="py-2 px-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleCheck(`costo:${key}`)}
                      className={`w-5 h-5 rounded border flex items-center justify-center ${
                        checkKey(`costo:${key}`)
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {checkKey(`costo:${key}`) && <Check className="h-3 w-3" />}
                    </button>
                  </td>
                  <td className="py-2 px-4 flex items-center gap-2">
                    {label}
                    {manual && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        (manual)
                      </span>
                    )}
                    <Eye className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </td>
                  <td className="py-2 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                    {manual ? (
                      <input
                        type="number"
                        value={manualValues[`${monthKey}:${key}`] ?? ''}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          setManualValue(key, v);
                        }}
                        className="w-28 text-right font-mono border border-gray-200 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800"
                        placeholder="0"
                      />
                    ) : (
                      <span className="font-mono">
                        {formatCurrency(eerr.costos[key] ?? 0)}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">
                    {totalIngresos > 0
                      ? (
                          ((eerr.costos[key] ?? 0) / totalIngresos) *
                          100
                        ).toFixed(1) + '%'
                      : '-'}
                  </td>
                </tr>
              ))}
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/60 font-medium">
                <td className="py-3 px-4" />
                <td className="py-3 px-4">Total Costos</td>
                <td className="py-3 px-4 text-right font-mono">
                  {formatCurrency(totalCostos)}
                </td>
                <td className="py-3 px-4 text-right">-</td>
              </tr>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-emerald-50/50 dark:bg-emerald-900/20 font-medium">
                <td className="py-3 px-4" />
                <td className="py-3 px-4">Utilidad Bruta</td>
                <td className="py-3 px-4 text-right font-mono text-emerald-700 dark:text-emerald-400">
                  {formatCurrency(utilidadBruta)}
                </td>
                <td className="py-3 px-4 text-right">
                  {totalIngresos > 0
                    ? ((utilidadBruta / totalIngresos) * 100).toFixed(1) + '%'
                    : '-'}
                </td>
              </tr>

              <tr className="border-b border-gray-100 dark:border-gray-700/50">
                <td colSpan={4} className="py-2 px-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/80">
                  GASTOS
                </td>
              </tr>
              {Object.entries(eerr.gastosByCategory).length === 0 ? (
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <td className="py-2 px-4" />
                  <td colSpan={3} className="py-2 px-4 text-gray-500 dark:text-gray-400">
                    Sin gastos en el período
                  </td>
                </tr>
              ) : (
                Object.entries(eerr.gastosByCategory)
                  .sort(([, a], [, b]) => b - a)
                  .map(([cat, amt]) => (
                    <tr
                      key={cat}
                      onClick={() =>
                        setDetailModal({ type: 'gasto', key: cat, label: cat })
                      }
                      className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 cursor-pointer group"
                    >
                      <td className="py-2 px-4" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => toggleCheck(`gasto:${cat}`)}
                          className={`w-5 h-5 rounded border flex items-center justify-center ${
                            checkKey(`gasto:${cat}`)
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          {checkKey(`gasto:${cat}`) && (
                            <Check className="h-3 w-3" />
                          )}
                        </button>
                      </td>
                      <td className="py-2 px-4 flex items-center gap-1">
                        {cat}
                        <Eye className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </td>
                      <td className="py-2 px-4 text-right font-mono">
                        {formatCurrency(amt)}
                      </td>
                      <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">
                        {totalIngresos > 0
                          ? ((amt / totalIngresos) * 100).toFixed(1) + '%'
                          : '-'}
                      </td>
                    </tr>
                  ))
              )}
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/60 font-medium">
                <td className="py-3 px-4" />
                <td className="py-3 px-4">Total Gastos</td>
                <td className="py-3 px-4 text-right font-mono">
                  {formatCurrency(totalGastos)}
                </td>
                <td className="py-3 px-4 text-right">-</td>
              </tr>
              <tr className="bg-emerald-100/50 dark:bg-emerald-900/30 font-semibold">
                <td className="py-3 px-4" />
                <td className="py-3 px-4">Utilidad Neta</td>
                <td
                  className={`py-3 px-4 text-right font-mono ${
                    utilidadNeta >= 0
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {formatCurrency(utilidadNeta)}
                </td>
                <td className="py-3 px-4 text-right">
                  {totalIngresos > 0
                    ? ((utilidadNeta / totalIngresos) * 100).toFixed(1) + '%'
                    : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de detalle */}
      <Dialog.Root
        open={!!detailModal}
        onOpenChange={(open) => !open && setDetailModal(null)}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
          <Dialog.Content
            className="fixed left-[50%] top-[50%] z-[100] max-h-[85vh] w-[95vw] max-w-[560px] translate-x-[-50%] translate-y-[-50%] rounded-xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden flex flex-col"
            aria-describedby={undefined}
          >
            {detailModal && (
              <>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                    Detalle: {detailModal.label}
                  </Dialog.Title>
                  <button
                    onClick={() => setDetailModal(null)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {(() => {
                    const items = getDetailItems(
                      detailModal.type,
                      detailModal.key
                    );
                    const isManual =
                      detailModal.type === 'costo' &&
                      [
                        'cmv_express',
                        'cmv_express_sample',
                        'cmv_notmerch',
                        'otros_costos',
                      ].includes(detailModal.key);
                    if (isManual) {
                      return (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                          Este valor se ingresa manualmente. No hay ítems
                          desglosados en el sistema.
                        </p>
                      );
                    }
                    if (!items || items.length === 0) {
                      return (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                          No hay ítems en este período.
                        </p>
                      );
                    }
                    const total = items.reduce((s, i) => s + i.amount, 0);
                    return (
                      <div className="space-y-3">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          {items.length} ítem(s) · Total:{' '}
                          {formatCurrency(total)}
                        </div>
                        <div className="space-y-2">
                          {items.map((item) => (
                            <div
                              key={item.id}
                              className="flex justify-between items-start gap-3 py-2.5 px-3 rounded-lg bg-gray-50 dark:bg-gray-700/60 border border-gray-100 dark:border-gray-600/50"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {item.desc}
                                </p>
                                {item.sub && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                    {item.sub}
                                  </p>
                                )}
                                {item.date && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    {formatArgentinaDate(item.date)}
                                  </p>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-sm font-mono font-medium text-gray-900 dark:text-white">
                                  {formatCurrency(item.amount)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default EstadoResultados;
